import React from 'react';
import Numbers from '~/utils/numbers';
import Dates from '~/utils/dates';
import './styles.css';

type MemoryCalculationModalProps = {
	prelim?: any;
	inputData?: any;
	totalValueFree?: number;
	totalValueCaptive?: number;
	onClose?: () => void;
};

const formatCurrency = (value: number | string | undefined) => {
	if (value === null || value === undefined || value === '') return '-';
	const n = typeof value === 'string' ? parseFloat(value) : value;
	return Numbers.money(isFinite(n as number) ? (n as number) : 0, false, true);
};

const formatNumber = (value: number | string | undefined, digits = 5) => {
	if (value === null || value === undefined || value === '') return '-';
	const n = typeof value === 'string' ? parseFloat(value) : value;
	return isFinite(n as number) ? (n as number).toFixed(digits) : '-';
};

const formatPercent = (value: number | string | undefined, digits = 2) => {
	if (value === null || value === undefined || value === '') return '-';
	const n = typeof value === 'string' ? parseFloat(value) : value;
	return isFinite(n as number) ? `${(n as number).toFixed(digits)}%` : '-';
};

const MemoryCalculationModal: React.FC<MemoryCalculationModalProps> = ({ inputData, totalValueFree, totalValueCaptive, onClose }) => {
	const data = inputData;
	const monthLabel = typeof data?.month === 'number' ? Dates._getNameMonth(data.month, true) : '-';
	const unitType = data?.unit_type === 'retailer' ? 'Varejista' : 'Atacadista';
	const priceType = data?.price_type === 'guaranteedDiscount' ? 'Desconto garantido' : 'Preço Fixo';

	return (
		<div className="memory-calc-modal">

			{/* Seção: Unidade */}
			<div className="mc-section">
				<div className="mc-section-title">Unidade</div>
				<div className="memory-calc-grid">
					<div className="mc-row">
						<span className="mc-label"><b>Ano</b></span>
						<div className="mc-value">{data?.year ?? '-'}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label"><b>Mês</b></span>
						<div className="mc-value">{monthLabel}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label"><b>ID da unidade</b></span>
						<div className="mc-value">{data?.unit_id ?? '-'}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label"><b>Tipo da unidade</b></span>
						<div className="mc-value">{unitType}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label"><b>Tipo de preço</b></span>
						<div className="mc-value">{priceType}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label"><b>Modalidade (fatura)</b></span>
						<div className="mc-value">{data?.modality_invoice ?? '-'}</div>
					</div>
				</div>
			</div>
			{/* Seção: Volume */}
			<div className="mc-section">
				<div className="mc-section-title">Volume</div>
				<div className="memory-calc-grid">
					<div className="mc-row">
						<span className="mc-label">Consumo Medido(kWh)</span>
						<div className="mc-value">{formatNumber(data?.consumptionMeasuredAmount, 3)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Percentual de Consumo em Ponta</span>
						<div className="mc-value">{formatPercent(data?.peakConsumptionPercentage)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Consumo Ponta (kWh)</span>
						<div className="mc-value">{formatNumber(data?.peakConsumptionAmount, 3)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Consumo Fora Ponta (kWh)</span>
						<div className="mc-value">{formatNumber(data?.offPeakConsumptionAmount, 3)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Demanda Contratada Ponta (kW)</span>
						<div className="mc-value">{formatNumber(data?.contractedDemandPeakAmount, 0)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Demanda Contratada Fora Ponta (kW)</span>
						<div className="mc-value">{formatNumber(data?.contractedDemandOffPeakAmount, 0)}</div>
					</div>
				</div>
			</div>

			{/* Seção: Tarifas */}
			<div className="mc-section">
				<div className="mc-section-title">Tarifas</div>
				<div className="memory-calc-grid">
				<div className="mc-row">
					<span className="mc-label">ICMS</span>
					<div className="mc-value">{formatPercent(data?.icmsPercentage)}</div>
				</div>
				<div className="mc-row">
					<span className="mc-label">Perdas</span>
					<div className="mc-value">{formatPercent(data?.lossesPercentage)}</div>
				</div>
				<div className="mc-row">
					<span className="mc-label">PROINFA</span>
					<div className="mc-value">{formatNumber(data?.proinfaAmount, 3)}</div>
				</div>
				<div className="mc-row">
					<span className="mc-label">Bandeira Tarifária</span>
					<div className="mc-value">{formatNumber(data?.tariffFlag, 5)}</div>
				</div>
				<div className="mc-row">
						<span className="mc-label">PIS/COFINS</span>
						<div className="mc-value">{formatPercent(data?.pisCofinsPercentage)}</div>
				</div>
				</div>
				<hr/>
				<div className="memory-calc-grid">
				<div className="mc-row">
					<span className="mc-label">(TUSD) Ponta</span>
					<div className="mc-value">{formatNumber(data?.tusd_peak_tariff, 5)}</div>
				</div>
				<div className="mc-row">
					<span className="mc-label">(TUSD) Fora Ponta</span>
					<div className="mc-value">{formatNumber(data?.tusd_off_peak_tariff, 5)}</div>
				</div>
				<div className="mc-row">
					<span className="mc-label">(TE) Ponta</span>
					<div className="mc-value">{formatNumber(data?.te_peak_tariff, 5)}</div>
				</div>
				<div className="mc-row">
					<span className="mc-label">(TE) Fora Ponta</span>
					<div className="mc-value">{formatNumber(data?.te_off_peak_tariff, 5)}</div>
				</div>
				</div>
			</div>

			{/* Seção: Valores adicionais */}
			<div className="mc-section">
				<div className="mc-section-title">Valores adicionais</div>
				<div className="memory-calc-grid">
					<div className="mc-row">
						<span className="mc-label">Reserva de Capacidade</span>
						<div className="mc-value">{formatCurrency(data?.ercapValue)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Taxa CCEE</span>
						<div className="mc-value">{formatCurrency(data?.cceeFeeValue)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Reserva de Energia</span>
						<div className="mc-value">{formatCurrency(data?.reserveEnergyValue)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Liquidação Financeira</span>
						<div className="mc-value">{formatCurrency(data?.financialSettlementValue)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Liquidação Nuclear</span>
						<div className="mc-value">{formatCurrency(data?.nuclearEnergyValue)}</div>
					</div>
				</div>
				</div>

				<div className="mc-section">
				<div className="mc-section-title">Resultados finais</div>
				<div className="memory-calc-grid">
					<div className="mc-row">
						<span className="mc-label">Preço de Faturamento (R$)</span>
						<div className="mc-value">{formatCurrency(data?.price_billing_value)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Total valor livre (R$)</span>
						<div className="mc-value">{formatCurrency(totalValueFree)}</div>
					</div>
					<div className="mc-row">
						<span className="mc-label">Total valor captivo (R$)</span>
						<div className="mc-value">{formatCurrency(totalValueCaptive)}</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MemoryCalculationModal;

